import { bffMutation } from '@parsina/framework/clinical';

/**
 * Join the doctor to the clinic they just founded.
 *
 * ── Why this is a step at all ──────────────────────────────────────────────
 *
 * A clinic born in THIS panel writes clinicmanagement `clinics` + `clinic_staff`
 * and a Keycloak clinic role. It does not write a doctorpreference
 * `clinic_doctors` row and does not grant the `doctor` role — those are the
 * doctor side's, and `createClinic` has no business reaching into it. So a
 * doctor who registers their own clinic here is, to every doctor-side read,
 * a practitioner of nowhere: `myClinics` comes back empty and the clinic
 * filters, the contracts and the presence hours all say «no clinic».
 *
 * `enterPanelWithOwnedClinic` is the doctor BFF's repair for exactly that. It
 * takes no arguments — it reads the clinics whose `keycloak_user_id` is this
 * token's `sub`, which is what `createClinic` stamped — links EVERY one of them
 * rather than only the newest, accepts the membership (this is the founder's
 * own clinic; there is nobody to approve it) and grants the `doctor` role.
 *
 * ── It is idempotent, and that is load-bearing ─────────────────────────────
 *
 * The resolver skips a clinic it already linked, so running it twice links
 * nothing twice. That is what lets this be retried from the wizard and re-run
 * on a later sign-in without a second copy of the practitioner appearing in the
 * clinic's «پرسنل» list.
 *
 * ── Why the failure is not fatal ───────────────────────────────────────────
 *
 * The clinic exists and is enterable by the time this runs. A doctor blocked
 * from their own reception desk because a doctorpreference write failed would
 * be a worse outcome than a doctor who has to link later, so the caller reports
 * it and walks on.
 */
const ENTER_PANEL_WITH_OWNED_CLINIC = `
  mutation EnterPanelWithOwnedClinic {
    enterPanelWithOwnedClinic {
      id clinicId doctorId status registeredVia
      clinic { id name }
    }
  }
`;

/**
 * @returns {Promise<object|null>} the accepted link, or null if the doctor BFF
 *   refused — never throws, because nothing downstream of it may be skipped.
 */
export async function linkFounderAsPractitioner() {
  try {
    const res = await bffMutation(ENTER_PANEL_WITH_OWNED_CLINIC);
    return res?.enterPanelWithOwnedClinic || null;
  } catch (err) {
    // Named in the console rather than swallowed: when a founder later reports
    // «پنل پزشک می‌گوید عضو هیچ کلینیکی نیستم», this line is the evidence.
    console.error('[practitionerLink] enterPanelWithOwnedClinic failed:', err?.message || err);
    return null;
  }
}

export default linkFounderAsPractitioner;
